import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ConcertDatesPage() {
  const { pageId } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API}/public/concerts/${pageId}`);
        if (response.ok) {
          const data = await response.json();
          setPageData(data.page);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching concert page:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pageId]);

  const parseContent = (text) => {
    if (!text) return [];
    return text.split('\n').filter(line => line.trim());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-lime-400 mb-4" style={{ fontFamily: 'Raleway, sans-serif' }}>Kuroneko</h1>
          <p className="text-gray-400 text-lg">Page non trouvée</p>
        </div>
      </div>
    );
  }

  const lines = parseContent(pageData.concert_text);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-3 drop-shadow-lg" style={{ fontFamily: 'Raleway, sans-serif', color: '#4DBA5F' }}>
              Kuroneko
            </h1>
            <div className="w-24 h-0.5 bg-lime-400 mx-auto mb-6 opacity-50"></div>
            {pageData.artist && (
              <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Raleway, sans-serif' }}>
                {pageData.artist}
              </h2>
            )}
            {pageData.album && (
              <p className="text-lg text-gray-400 italic">{pageData.album}</p>
            )}
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h3 className="text-xl font-bold text-lime-400 tracking-widest uppercase">
              Dates de concerts
            </h3>
            <div className="w-16 h-0.5 bg-lime-400/30 mx-auto mt-2"></div>
          </div>

          {/* Free text */}
          {pageData.free_text && (
            <div className="mb-8 bg-gray-800/40 border border-gray-700/50 rounded-xl p-6">
              <p className="text-gray-300 leading-relaxed text-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
                {pageData.free_text}
              </p>
            </div>
          )}

          {/* Concert dates list */}
          <div className="space-y-1">
            {lines.map((line, index) => (
              <div
                key={index}
                className="group flex items-center gap-4 px-6 py-3 rounded-lg transition-all duration-200 hover:bg-gray-800/40"
                style={{
                  animationDelay: `${index * 50}ms`,
                  animation: 'fadeInUp 0.4s ease-out forwards',
                  opacity: 0
                }}
              >
                <div className="w-2 h-2 rounded-full bg-lime-400 flex-shrink-0 group-hover:scale-125 transition-transform"></div>
                <p className="text-gray-200 text-base" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  <span className="font-bold text-white">{line.split(' - ')[0]}</span>
                  {line.includes(' - ') && <span> - {line.split(' - ').slice(1).join(' - ')}</span>}
                </p>
              </div>
            ))}
          </div>

          {lines.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>Aucune date de concert disponible</p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-16 text-center">
            <div className="w-16 h-0.5 bg-gray-700 mx-auto mb-6"></div>
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Raleway, sans-serif' }}>
              kuroneko-distribution.com
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default ConcertDatesPage;
